import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { getFirestore, collection, query, orderBy, limit, getDocs } from 'firebase/firestore';
import EmailCapture from './EmailCapture';
import './App.css';

const MANIFEST_URL = '/blog-content/_index.json';

function formatDate(iso) {
  if (!iso) return '';
  // Accept both ISO strings (static manifest) and Date instances (Firestore).
  const d = iso instanceof Date ? iso : new Date(typeof iso === 'string' ? iso + 'T00:00:00Z' : iso);
  if (Number.isNaN(d.getTime())) return typeof iso === 'string' ? iso : '';
  // A calendar date read as UTC midnight: format it in UTC too, or every reader west of
  // Greenwich sees the day before.
  return d.toLocaleDateString(undefined, { year: 'numeric', month: 'long', day: 'numeric', timeZone: 'UTC' });
}

// Coerce a Firestore Timestamp (or ISO string, or Date) into a sortable ISO date.
function toIsoDate(v) {
  if (!v) return '';
  if (typeof v === 'string') return v;
  if (v instanceof Date) return v.toISOString().slice(0, 10);
  if (typeof v.toDate === 'function') return v.toDate().toISOString().slice(0, 10);
  if (v.seconds) return new Date(v.seconds * 1000).toISOString().slice(0, 10);
  return '';
}

const BlogPage = () => {
  const [manifest, setManifest] = useState(null);
  const [firestorePosts, setFirestorePosts] = useState([]);
  const [error, setError] = useState(null);

  useEffect(() => {
    window.scrollTo(0, 0);
    let cancelled = false;
    (async () => {
      // Static manifest (legacy git-committed posts)
      try {
        const res = await fetch(MANIFEST_URL, { cache: 'no-cache' });
        if (res.ok) {
          const data = await res.json();
          if (!cancelled) setManifest(data);
        }
      } catch (err) {
        if (!cancelled) setError(err.message || String(err));
      }
      // Firestore-published posts (Phase Blog — admin-published from /admin/blog)
      try {
        const db = getFirestore();
        const q = query(collection(db, 'blog_posts'), orderBy('publishedAt', 'desc'), limit(200));
        const snap = await getDocs(q);
        if (!cancelled) setFirestorePosts(snap.docs.map(d => ({ id: d.id, ...d.data() })));
      } catch (err) {
        // Firestore posts are best-effort — static manifest still renders if this fails.
        // eslint-disable-next-line no-console
        console.warn('[BlogPage] Firestore posts fetch failed:', err);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  // Merge: Firestore posts take precedence on slug collision (so re-published posts win).
  const staticPosts = (manifest?.posts || []).map(p => ({ ...p, _source: 'static' }));
  const dynamicPosts = firestorePosts.map(p => ({
    ...p,
    publishedAt: toIsoDate(p.publishedAt),
    _source: 'firestore',
  }));
  const seen = new Set(dynamicPosts.map(p => p.slug));
  const merged = [...dynamicPosts, ...staticPosts.filter(p => !seen.has(p.slug))];
  const posts = merged.sort((a, b) => (b.publishedAt || '').localeCompare(a.publishedAt || ''));

  return (
    <div className="mp-page" style={{ background: '#fff', minHeight: 'calc(100vh - 60px)' }}>
      <section style={{ maxWidth: 960, margin: '0 auto', padding: '64px 24px 24px' }}>
        <span className="trust-badge trust-official" style={{ display: 'inline-block', marginBottom: 16 }}>
          Blog
        </span>
        <h1 style={{ fontSize: '2.6rem', fontWeight: 800, color: '#111827', margin: '0 0 16px', lineHeight: 1.1 }}>
          {manifest?.siteTitle || 'mChatAI Blog'}
        </h1>
        <p style={{ fontSize: '1.1rem', color: '#6b7280', lineHeight: 1.6, margin: 0 }}>
          {manifest?.siteDescription || 'Notes from the workshop.'}
        </p>
      </section>

      <section style={{ maxWidth: 960, margin: '0 auto', padding: '24px 24px 64px' }}>
        {error && (
          <div style={{
            background: '#fef2f2', border: '1px solid #fecaca', color: '#991b1b',
            padding: 16, borderRadius: 8, marginBottom: 24
          }}>
            Failed to load posts: {error}
          </div>
        )}

        {!manifest && !error && (
          <p style={{ color: '#6b7280' }}>Loading posts…</p>
        )}

        {manifest && posts.length === 0 && (
          <p style={{ color: '#6b7280' }}>
            No posts yet — the first one is on the way.
          </p>
        )}

        <div style={{ display: 'grid', gap: 20 }}>
          {posts.map((post) => (
            <article
              key={post.slug}
              style={{
                padding: 24,
                border: '1px solid #e5e7eb',
                borderRadius: 12,
                background: '#fff',
                transition: 'box-shadow 0.15s, transform 0.15s'
              }}
              onMouseOver={(e) => {
                e.currentTarget.style.boxShadow = '0 8px 24px rgba(0,0,0,0.06)';
                e.currentTarget.style.transform = 'translateY(-2px)';
              }}
              onMouseOut={(e) => {
                e.currentTarget.style.boxShadow = 'none';
                e.currentTarget.style.transform = 'translateY(0)';
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 8, color: '#6b7280', fontSize: '0.85rem' }}>
                {post.series && (
                  <span style={{ background: '#eff6ff', color: '#1d4ed8', padding: '2px 10px', borderRadius: 999, fontSize: '0.75rem', fontWeight: 600 }}>
                    {post.series}
                  </span>
                )}
                <span>{formatDate(post.publishedAt)}</span>
                {post.readingMinutes && (
                  <span>· {post.readingMinutes} min read</span>
                )}
              </div>
              <h2 style={{ fontSize: '1.5rem', fontWeight: 700, color: '#111827', margin: '0 0 8px', lineHeight: 1.25 }}>
                <Link to={`/blog/${post.slug}`} style={{ color: '#111827', textDecoration: 'none' }}>
                  {post.title}
                </Link>
              </h2>
              <p style={{ color: '#4b5563', lineHeight: 1.6, margin: '0 0 12px' }}>
                {post.summary}
              </p>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                  {(post.tags || []).map((tag) => (
                    <span key={tag} style={{
                      fontSize: '0.72rem', color: '#6b7280',
                      background: '#f3f4f6', padding: '2px 8px', borderRadius: 4
                    }}>
                      #{tag}
                    </span>
                  ))}
                </div>
                <Link to={`/blog/${post.slug}`} style={{ color: '#3b82f6', textDecoration: 'none', fontSize: '0.9rem', fontWeight: 600 }}>
                  Read →
                </Link>
              </div>
            </article>
          ))}
        </div>
      </section>

      <section style={{ maxWidth: 720, margin: '0 auto', padding: '48px 24px 96px' }}>
        <EmailCapture
          source="blog-index-footer"
          title="Get the Weekly Build"
          subtitle="One short email each Sunday: this week’s recipe, top community build, and a tip."
        />
      </section>
    </div>
  );
};

export default BlogPage;
